import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import api, { Client, LeadGenProperty } from '../services/api';

type AppMode = 'internal' | 'client' | 'leadgen';

interface ClientContextType {
  clients: Client[];
  selectedClientId: string | null;  // null means "All Clients"
  selectedClient: Client | null;
  setSelectedClientId: (id: string | null) => void;
  isLoading: boolean;
  refreshClients: () => Promise<void>;
  // New mode system
  mode: AppMode;
  setMode: (mode: AppMode) => void;
  // Backward compat
  isInternalMode: boolean;
  setInternalMode: (internal: boolean) => void;
  // Lead gen state
  leadgenProperties: LeadGenProperty[];
  selectedPropertyId: string | null;
  setSelectedPropertyId: (id: string | null) => void;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

interface ClientProviderProps {
  children: ReactNode;
}

const resolveInitialMode = (): AppMode => {
  // Check new key first
  const storedMode = localStorage.getItem('appMode');
  if (storedMode === 'internal' || storedMode === 'client' || storedMode === 'leadgen') {
    return storedMode;
  }
  // Fall back to old key for backward compat
  const oldStored = localStorage.getItem('isInternalMode');
  if (oldStored !== null) {
    return oldStored === 'true' ? 'internal' : 'client';
  }
  return 'internal';
};

export const ClientProvider: React.FC<ClientProviderProps> = ({ children }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientIdState] = useState<string | null>(() => { const stored = localStorage.getItem('selectedClientId'); return stored && stored !== 'null' ? stored : null; });
  const [isLoading, setIsLoading] = useState(true);
  const [mode, setModeState] = useState<AppMode>(resolveInitialMode);

  // Lead gen state
  const [leadgenProperties, setLeadgenProperties] = useState<LeadGenProperty[]>([]);
  const [selectedPropertyId, setSelectedPropertyIdState] = useState<string | null>(() => {
    const stored = localStorage.getItem('selectedPropertyId');
    return stored && stored !== 'null' ? stored : null;
  });

  const refreshClients = useCallback(async () => {
    try {
      setIsLoading(true);
      // Get all clients (active and internal)
      const response = await api.getClients();
      // Filter to only show active and internal clients in the dropdown
      const filteredClients = response.results.filter(
        c => c.status === 'active' || c.status === 'internal'
      );
      setClients(filteredClients);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load clients on mount
  useEffect(() => {
    refreshClients();
  }, [refreshClients]);

  // Load leadgen properties on mount
  useEffect(() => {
    const loadProperties = async () => {
      try {
        const properties = await api.getLeadGenProperties();
        setLeadgenProperties(properties);
      } catch (error) {
        console.error('Failed to load leadgen properties:', error);
      }
    };
    loadProperties();
  }, []);

  // Restore selection and mode from localStorage
  useEffect(() => {
    const storedClientId = localStorage.getItem('selectedClientId');
    if (storedClientId) {
      setSelectedClientIdState(storedClientId === 'null' ? null : storedClientId);
    }
  }, []);

  // Persist selection to localStorage
  const setSelectedClientId = useCallback((id: string | null) => {
    setSelectedClientIdState(id);
    localStorage.setItem('selectedClientId', id ?? 'null');
  }, []);

  // Persist property selection to localStorage
  const setSelectedPropertyId = useCallback((id: string | null) => {
    setSelectedPropertyIdState(id);
    localStorage.setItem('selectedPropertyId', id ?? 'null');
  }, []);

  // Set mode and persist to localStorage
  const setMode = useCallback((newMode: AppMode) => {
    setModeState(newMode);
    localStorage.setItem('appMode', newMode);
    // Also update old key for backward compat
    localStorage.setItem('isInternalMode', String(newMode === 'internal'));

    if (newMode === 'internal') {
      // When switching to internal mode, clear client selection
      setSelectedClientIdState(null);
      localStorage.setItem('selectedClientId', 'null');
      // Clear property selection
      setSelectedPropertyIdState(null);
      localStorage.setItem('selectedPropertyId', 'null');
    } else if (newMode === 'client') {
      // Clear property selection
      setSelectedPropertyIdState(null);
      localStorage.setItem('selectedPropertyId', 'null');
    } else if (newMode === 'leadgen') {
      // Clear client selection
      setSelectedClientIdState(null);
      localStorage.setItem('selectedClientId', 'null');
    }
  }, []);

  // Backward compat wrappers
  const isInternalMode = mode === 'internal';
  const setInternalMode = useCallback((internal: boolean) => {
    setMode(internal ? 'internal' : 'client');
  }, [setMode]);

  const selectedClient = selectedClientId
    ? clients.find(c => c.id === selectedClientId) || null
    : null;

  return (
    <ClientContext.Provider value={{
      clients,
      selectedClientId,
      selectedClient,
      setSelectedClientId,
      isLoading,
      refreshClients,
      mode,
      setMode,
      isInternalMode,
      setInternalMode,
      leadgenProperties,
      selectedPropertyId,
      setSelectedPropertyId
    }}>
      {children}
    </ClientContext.Provider>
  );
};
